import CampaignIcon from '@mui/icons-material/Campaign';
import DraftsIcon from '@mui/icons-material/Drafts';
import InboxIcon from '@mui/icons-material/Inbox';
import SendIcon from '@mui/icons-material/Send';
import { Box, Grid, Link, ListItem, styled, ListItemIcon, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
  getEmailStatistics,
  getSentEmailLogs,
  IEmailLogDTO,
  IEmailLogPayload,
  IEmailStatisticsDTO,
  amplitude,
} from 'wisestep-api';
import {
  Item,
  WsButton,
  WsTitleBanner,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsColors,
} from 'wisestep-base';

import ComposeEmail from '../../../components/ComposeEmail';
import { useUserContext, useWorkspaceContext } from '../../../context';

import CampaignEmailLogs from './CampaignEmailLogs';
import SentEmailLogs from './SentEmailLogs';
import { SentEmailStats } from './SentEmailStats';
import { InboxStats } from './twoWayEmailSync/components/InboxStats';
import { InboxActionType } from './twoWayEmailSync/helper';
import InboxEmailLogs from './twoWayEmailSync/InboxEmailLogs';
import { useNewEmailStore } from './twoWayEmailSync/Util/NewEmailStore';

const IndexItem = styled(ListItem)(() => ({
  '.Mui-selected': {
    backgroundColor: WsColors.BorderGrey,
  },
  ':hover': {
    backgroundColor: WsColors.TheBlues50,
  },
  cursor: 'pointer',
  paddingRight: '16px',
}));

export const SmallBoxWithRadius = styled(Box)(() => ({
  border: '1px solid ',
  borderColor: WsColors.TheBlues500,
  backgroundColor: WsColors.White,
  borderRadius: 8,
  paddingLeft: 12,
  paddingRight: 8,
  height: 32,
  marginBottom: 6,
  marginRight: 6,
  alignItems: 'center',
}));

export interface IEmailOption {
  label: string;
  icon?: ReactNode;
  url?: string;
  count?: number | string;
}
export interface ComponentSingleStateType {
  selectedOption: number;
  showComp: ReactNode;
  title: string;
}

export interface ISentEmailStatistics {
  sent?: number | string;
  failed?: number | string;
}

const WsDataLog = () => {
  const setUnreadCount = useNewEmailStore((state) => state.setUnreadCount);
  const configuredImapAccounts = useNewEmailStore((state) => state.configuredImapAccounts);
  const [inboxStats, setInboxStats] = useState<IEmailStatisticsDTO>({
    unread: 0,
    read: 0,
    total: 0,
  });
  const admin = useUserContext();
  const workspace = useWorkspaceContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const options = [
    {
      icon: <InboxIcon />,
      label: 'Inbox',
      url: '/email-settings/logs/inbox/',
      count: inboxStats.unread,
    },
    {
      icon: <SendIcon />,
      label: 'Sent',
      url: '/email-settings/logs/sent/',
    },
    {
      icon: <DraftsIcon />,
      label: 'Draft',
    },
    {
      icon: <CampaignIcon />,
      label: 'Campaign Emails',
      url: '/email-settings/logs/campaigns/',
    },
  ];

  const [statistics, setStatistics] = useState<ISentEmailStatistics>({
    sent: 0,
    failed: 0,
  });
  const [showDrawer, setShowDrawer] = useState<{ showDrawer: boolean; hasAttachments: boolean }>({
    showDrawer: false,
    hasAttachments: false,
  });

  const [componentSingleState, setComponentSingleState] = useState<ComponentSingleStateType>({
    selectedOption: 0,
    title: 'Inbox',
    showComp: <SentEmailLogs />,
  });
  const [formData, setFormData] = useState<InboxActionType>({
    to: [],
    subject: '',
    body: '',
    showForm: false,
  });

  const filterAmplitudeEvent = (name: string, clickedOnName: string) =>
    amplitude(name, {
      clickedOn: clickedOnName,
      workspaceName: workspace.name,
      pageName: 'Mail',
      planType: workspace.planType,
      interfaceType: 'web',
      pageUrl: window.location.href,
    });
  useEffect(() => {
    const getEmailLogs = async () => {
      const payload: IEmailLogPayload = {
        page: 0,
        size: 1,
        range: '1d',
      };
      const response = await getSentEmailLogs(payload);
      const logs: IEmailLogDTO = response.data;
      if (logs) {
        setStatistics({
          sent: Number(logs.aggregationResult?.Sent?.true ?? 0),
          failed: Number(logs.aggregationResult?.Error?.true ?? 0),
        });
      }
    };
    getEmailLogs();
  }, []);

  const getInboxStats = useCallback(
    async (email: string) => {
      const inboxStatsResponse = await getEmailStatistics(email);
      if (inboxStatsResponse.data) {
        setInboxStats(inboxStatsResponse.data);
        setUnreadCount(inboxStatsResponse.data.unread);
      }
    },
    [setUnreadCount]
  );
  useEffect(() => {
    switch (pathname) {
      case '/email-settings/logs/inbox/':
        setComponentSingleState({
          selectedOption: 0,
          showComp: (
            <InboxEmailLogs
              onActionCallback={(data) => {
                setFormData(data);
                setShowDrawer((prev) => ({ ...prev, showDrawer: data.showForm }));
              }}
              hasAttachmentsCallback={(hasAttachments: boolean) =>
                setShowDrawer((prev) => ({ ...prev, hasAttachments: hasAttachments }))
              }
              getInboxStats={getInboxStats}
            />
          ),
          title: 'Inbox',
        });

        break;
      case '/email-settings/logs/sent/':
        setComponentSingleState({
          selectedOption: 1,
          showComp: <SentEmailLogs />,
          title: 'Sent Email',
        });
        break;
      case '/email-settings/logs/campaigns/':
        setComponentSingleState({
          selectedOption: 3,
          showComp: <CampaignEmailLogs />,
          title: 'Campaign Email',
        });
    }
  }, [admin.userDTO?.email, getInboxStats, pathname]);

  const URLBasedOptions = (option: IEmailOption) => (
    <Link component={RouterLink} to={option.url ?? './'} underline="none" color="inherit">
      <IndexItem
        selected={location.pathname === option.url}
        secondaryAction={<WsTypography>{option.count ?? ''}</WsTypography>}
      >
        <ListItemIcon>{option.icon}</ListItemIcon>
        <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.PRIMARY} cursor="pointer">
          {option.label}
        </WsTypography>
      </IndexItem>
    </Link>
  );

  const NonURLBasedOptions = (option: IEmailOption, i: number) => (
    <IndexItem
      selected={componentSingleState.selectedOption === i}
      secondaryAction={<WsTypography>{option.count ?? ''}</WsTypography>}
    >
      <Tooltip title="Coming Soon">
        <ListItemIcon>{option.icon}</ListItemIcon>
      </Tooltip>
      <WsTypography
        variant={WsTypoVariants.BODY1}
        color={WsTypoColors.PRIMARY}
        cursor="pointer"
        hoverText="Coming Soon"
      >
        {option.label}
      </WsTypography>
    </IndexItem>
  );

  return (
    <Box>
      <WsTitleBanner
        title={componentSingleState.title}
        goBack={() => navigate('/')}
        rightChildren={
          <>
            {componentSingleState.title === 'Sent Email' && (
              <SentEmailStats statistics={statistics} />
            )}
            {componentSingleState.title === 'Inbox' && <InboxStats inboxStats={inboxStats} />}
          </>
        }
      ></WsTitleBanner>

      <Grid item container xs={12} mt={1}>
        <Grid item xs={1.8}>
          <SmallBoxWithRadius>
            <Tooltip title={'Compose Email'}>
              <Box textAlign="center">
                <WsButton
                  onClick={() => {
                    filterAmplitudeEvent('Inbox_Compose_Email', 'Compose Email');
                    setShowDrawer((prev) => ({ ...prev, showDrawer: !showDrawer.showDrawer }));
                  }}
                  variant="text"
                  disabled={false}
                >
                  Compose Email
                </WsButton>
              </Box>
            </Tooltip>
          </SmallBoxWithRadius>
          <Item sx={{ p: 0, backgroundColor: WsColors.White, mr: 1 }}>
            {options.map((option, i) => (
              <Grid key={i} item xs={12}>
                {option.url ? URLBasedOptions(option) : NonURLBasedOptions(option, i)}
              </Grid>
            ))}
          </Item>
        </Grid>
        <Grid item xs={10.2} justifyContent="flex-end">
          {componentSingleState.showComp}
        </Grid>
      </Grid>
      <ComposeEmail
        showDrawer={showDrawer.showDrawer}
        emailData={{
          toEmailList: formData.to ?? [],
          subject: formData.subject ?? '',
          body: formData.body ?? '',
          bccList: !isEmpty(configuredImapAccounts)
            ? configuredImapAccounts[0].bcc?.split(' ')
            : [],
        }}
        hasAttachments={showDrawer.hasAttachments}
        onClose={() => setShowDrawer((prev) => ({ ...prev, showDrawer: !showDrawer.showDrawer }))}
        onAttachmentWarningClose={() =>
          setShowDrawer((prev) => ({ ...prev, hasAttachments: !showDrawer.hasAttachments }))
        }
      />
    </Box>
  );
};

export default WsDataLog;
